import type { Theme } from '@mui/material';
import { Box, Button, Menu, MenuItem, Tooltip, Typography } from '@mui/material';
import { type ComponentProps, useRef, useState } from 'react';
import { roundAndFormat } from '@/utils/helpers';
import { useValuationsStore } from '../../store/valuationsStore';
import { SORT_MENU_ITEMS, SORT_TYPES } from './constants';

const assetListSortStyles = {
  cursor: 'pointer',
  '& span': {
    padding: '0.12rem 0.25rem',
    borderRadius: '0.2rem',
    border: (theme: Theme) => `thin solid ${theme.palette.divider}`,
  },
  '&:hover': {
    '& span': {
      background: (theme: Theme) => theme.palette.action.hover,
    },
  },
};

const assetListHeaderStyles = {
  padding: '1rem',
  fontWeight: 600,
  borderBottom: (theme: Theme) => `1px solid ${theme.palette.baseColors.iceBlue.c100}`,
};

const AssetListSortDirection = () => {
  const setSortAscending = useValuationsStore.use.setSortAscending();

  const sortAscending = useValuationsStore.use.sortAscending();

  const handleSortClick = () => {
    setSortAscending(!sortAscending);
  };

  return (
    <Tooltip title='Sort List' aria-label='sort list'>
      <Button
        type='button'
        size='small'
        color='secondary'
        onClick={handleSortClick}
        sx={{
          minWidth: 0,
          height: 'auto',
          padding: '0.125rem 0.5rem',
          fontWeight: 'inherit',
        }}
        data-id='asset-list-sort-direction'
      >
        {sortAscending ? <span>↑</span> : <span>↓</span>}
      </Button>
    </Tooltip>
  );
};

const AssetListSorterBy = () => {
  const sortType = useValuationsStore.use.sortType();
  const setSortType = useValuationsStore.use.setSortType();
  const setSortAscending = useValuationsStore.use.setSortAscending();

  const sortByRef = useRef<HTMLSpanElement>(null);
  const [sortByOpen, setSortByOpen] = useState(false);

  const handleMenuClick = () => {
    setSortByOpen(true);
  };

  const handleMenuClose = (value?: (typeof SORT_MENU_ITEMS)[number]['value']) => {
    if (value !== undefined) {
      setSortType(value);
      setSortAscending(value.label === SORT_TYPES?.NAME?.label);
    }
    setSortByOpen(false);
  };

  return (
    <>
      <Tooltip title='Change Sort Type' aria-label='sort type'>
        <Typography
          ref={sortByRef}
          variant='caption'
          sx={assetListSortStyles}
          display='inline-block'
          onClick={handleMenuClick}
          color='text.primary'
          fontWeight='inherit'
          data-id='asset-list-sort-by'
        >
          SORT BY: <span>{sortType.label}</span>
        </Typography>
      </Tooltip>
      {sortByRef.current ? (
        <Menu
          anchorEl={sortByRef.current}
          keepMounted
          open={sortByOpen}
          onClose={() => handleMenuClose()}
          data-id='asset-list-sort-menu'
        >
          {SORT_MENU_ITEMS.map(item => (
            <MenuItem
              key={item?.value?.filter}
              onClick={() => handleMenuClose(item.value)}
              data-id='asset-list-sort-menu-item'
            >
              {item.label}
            </MenuItem>
          ))}
        </Menu>
      ) : null}
    </>
  );
};

const AssetListSorters = ({ children }: ComponentProps<typeof Box>) => {
  return (
    <Box display='flex' alignItems='center' gap={0.5} data-id='asset-list-sorters'>
      {children}
    </Box>
  );
};

const AssetListTotal = () => {
  const totalCount = useValuationsStore.use.totalCount();
  const loadingAssets = useValuationsStore.use.loadingAssets();
  const sortedAssets = useValuationsStore.use.sortedAssets();

  const getTotalAssets = () => {
    if (!loadingAssets && totalCount !== null) {
      return roundAndFormat(totalCount);
    }
    if (sortedAssets.length > 0) {
      return sortedAssets.length;
    }
    return '-';
  };

  if (loadingAssets) {
    return (
      <Typography variant='caption' data-id='asset-list-total-loading'>
        LOADING
      </Typography>
    );
  }

  return (
    <Typography
      color='text.primary'
      fontWeight='inherit'
      variant='caption'
      data-id='asset-list-total'
    >
      {getTotalAssets()} ASSETS
    </Typography>
  );
};

const AssetListHeader = () => {
  return (
    <Box
      sx={assetListHeaderStyles}
      display='flex'
      justifyContent='space-between'
      alignItems='center'
      position='sticky'
      data-id='asset-list-header'
    >
      <AssetListTotal />
      <AssetListSorters>
        <AssetListSorterBy />
        <AssetListSortDirection />
      </AssetListSorters>
    </Box>
  );
};

export { AssetListHeader };
